// The matchup cue (PLAN-2026-10-03-research-tools.md §4, scenario E6): how
// many points a player's opponent has allowed to his position this season, in
// this league's scoring, and where that ranks — on every row that shows his
// game, in one direction everywhere.
//
// ONE DIRECTION, 1 IS THE MOST ALLOWED. The big sites disagree (ESPN's rank
// runs high = easy, Yahoo's low = easy) and their users are confused by it.
// Here rank 1 is the club that has allowed the most, and a client says it as
// *most* or *fewest* (B-218) so nobody has to remember which way it runs.
//
// THE BAND IS WHICH THIRD THE FIGURE IS IN, named by the figure — `most`,
// `middle`, `fewest` — never by a grade. A client may tint by it, beside the
// figure in words and never instead of it; "tough", "easy", "start" and
// "avoid" are verdicts (docs/decisions/2026-09-12-tools-not-instructions.md,
// B-218, B-249).

import { z } from 'zod';

export const MatchupBand = z.enum(['most', 'middle', 'fewest']);
export type MatchupBand = z.infer<typeof MatchupBand>;

export const MatchupCue = z.object({
  /** Fantasy points a game the opponent has allowed to the position, in this league's scoring. */
  perGame: z.number(),
  /** 1 = the most allowed, of `of` clubs. */
  rank: z.number().int().min(1),
  of: z.number().int().min(1),
  /** How many games it rests on. Fewer than three is early, and a client says so. */
  games: z.number().int().min(1),
  /** Which third of the clubs by points allowed: `most`, `middle` or `fewest`. */
  band: MatchupBand,
});
export type MatchupCue = z.infer<typeof MatchupCue>;

/** Fewer games than this and a cue is early: drawn as such, and said. */
export const MATCHUP_EARLY_GAMES = 3;

/** The band for a rank: the top third `most`, the bottom third `fewest`, the rest `middle`. */
export function matchupBand(rank: number, of: number): MatchupBand {
  const third = Math.ceil(of / 3);
  if (rank <= third) return 'most';
  if (rank > of - third) return 'fewest';
  return 'middle';
}

/** A cue from the figures, or null when there are none (a bye, an opponent yet to play). */
export function matchupCue(
  versus: { perGame: number; rank: number; of: number; games: number } | null | undefined,
): MatchupCue | null {
  if (versus === null || versus === undefined) return null;
  return { ...versus, band: matchupBand(versus.rank, versus.of) };
}
